import type {ActionsObservable} from 'redux-observable';
import {Observable} from 'rxjs';
import {switchMap, merge, filter, map, catchError} from 'rxjs/operators';

import {serializeFilters} from 'nutshell-core';
import {fetchList} from 'nutshell-core/entities/fetch-list';
import {transformDataForReport} from 'nutshell-core/json-to-report-data';
import {safelyParseError} from 'nutshell-core/utils';

import {ENGAGEMENT_LEADS} from '../../report-types';

import {
    updateReportChartData,
    failReportChartData,
    updateListData,
    updateListSummaryData,
    failListData,
} from './leads-actions';
import type {
    EngagementLeadsReportDataRequestedAction,
    EngagementLeadsListRequestedAction,
} from './types';

const REST_URL = '/rest/leads/report';

export const requestEngagementLeadsReportDataEpic = (action$: ActionsObservable<any>) =>
    action$.ofType('ENGAGEMENT_LEADS_REPORT_DATA_REQUESTED').pipe(
        switchMap((action: EngagementLeadsReportDataRequestedAction) => {
            const {reportParams, filters} = action.payload;
            const params = getPostParams(reportParams, filters);
            const requestStream = Observable.from(fetchData(params));
            const reportDataStream = requestStream.pipe(map(transformDataForReport));

            const reportChartDataStream = reportDataStream.pipe(
                map((data) => data.chartData),
                filter((chartData) => Boolean(chartData)),
                map((chartData) => updateReportChartData({chartData}))
            );

            return reportChartDataStream.pipe(
                catchError((err) => Observable.of(failReportChartData(err)))
            );
        })
    );

export const requestEngagementLeadsListDataEpic = (action$: ActionsObservable<any>) =>
    action$.ofType('ENGAGEMENT_LEADS_LIST_DATA_REQUESTED').pipe(
        switchMap((action: EngagementLeadsListRequestedAction) => {
            const {filters, pageNum = 1, columns, sort} = action.payload;

            // Our pagination components aren't 0-based, but this specific
            // request expects page 0 to be the first page. Using Math.max() here
            // in case older URLs with page 0 are still in the wild
            const adjustedPageNum = Math.max(0, pageNum - 1);

            const filtersWithOrigin = addOriginFilter(filters);

            const listRequestStream = Observable.from(
                fetchList('leads', {
                    filter: filtersWithOrigin,
                    sort,
                    // @ts-expect-error
                    page: {page: adjustedPageNum},
                    fields: columns,
                    reportKey: ENGAGEMENT_LEADS,
                })
            );

            const leadListDataStream = listRequestStream.pipe(
                map((response) => updateListData(response))
            );
            const leadListSummaryDataStream = listRequestStream.pipe(
                map((response) => updateListSummaryData(response))
            );

            return leadListDataStream.pipe(
                merge(leadListSummaryDataStream),
                catchError((err) => {
                    const safeError = safelyParseError(err);

                    return Observable.of(failListData(safeError));
                })
            );
        })
    );

// @ts-expect-error
function addOriginFilter(filters) {
    const chatOrigin = NutshellFrontendSettings.originApiIdChat;
    const chatbotOrigin = NutshellFrontendSettings.originApiIdChatbot;

    const originData = [chatOrigin, chatbotOrigin]
        .filter(Boolean)
        .map((origin) => ({data: origin}));

    return [...filters, {origin: originData}];
}

// @ts-expect-error
function getPostParams(reportParams, filters) {
    const {gap, facet, segment, useConfidence, dateRangeType} = reportParams;

    const filtersWithOrigin = addOriginFilter(filters);

    return {
        filter: serializeFilters(filtersWithOrigin),
        groupByField: dateRangeType,
        pivotFacet: facet,
        reportType: ENGAGEMENT_LEADS,
        gap,
        useConfidence,
        segmentByField: segment,
    };
}

// @ts-expect-error
function fetchData(params) {
    return Promise.all([fetchReportData(params)]);
}

// @ts-expect-error
function fetchReportData(params) {
    const ajaxParams = {
        url: REST_URL,
        dataType: 'json',
        data: params,
    } as const;

    return $.ajax(ajaxParams) as any;
}
